import React, { useState, useContext } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { CircularProgress } from '@mui/material';
import { AuthContext } from '../../authContext/context';
import { postRequest } from '../../lib/api';

const Reports = () => {
    const authCtx = useContext(AuthContext);
    const [startDate, setStartDate] = useState(dayjs().subtract(7, 'days'));
    const [endDate, setEndDate] = useState(dayjs());
    const [status, setStatus] = useState('');
    const [productType, setProductType] = useState('');
    const [loading, setLoading] = useState(false);

    const isGeneralEnquiry = productType === 'general_enquiry';

    const handleStatusChange = (event) => {
        setStatus(event.target.value);
    };

    const handleProductTypeChange = (event) => {
        setProductType(event.target.value);
    };

    const handleGenerateReport = async () => {
        setLoading(true);
        try {
            const response = await postRequest('private/generateWeeklyReport', {
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
                status: isGeneralEnquiry ? undefined : (status || undefined),
                productType: productType || undefined
            }, authCtx.token);

            if (response?.data?.excelData) {
                // Decode base64 Excel data
                const binaryString = window.atob(response.data.excelData);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                // Create blob and download
                const blob = new Blob([bytes], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = response.data.fileName || `Lead_Overview_${startDate.format('YYYY-MM-DD')}_to_${endDate.format('YYYY-MM-DD')}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                alert(`Report generated successfully! ${response.data.totalRecords} records exported.`);
            } else if (response?.data?.downloadUrl) {
                // Legacy: Create a temporary link and click it to download the file
                const link = document.createElement('a');
                link.href = response.data.downloadUrl;
                link.download = `weekly_report_${startDate.format('YYYY-MM-DD')}_to_${endDate.format('YYYY-MM-DD')}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error('Error generating report:', error);
            alert('Failed to generate report. Please try again.');
        } finally {
            setLoading(false);
        }
    };


    return (
        <>
            <div className='page-header pb-3'>
                <h1 className='mt-0 mb-0'>Weekly Report</h1>
            </div>
            <div className='tab-content'>
                <div className='form-bg-view'>
                    <div className='mb-4'>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={['DatePicker']}>
                                <DatePicker
                                    sx={{ my: 1, width: '60%' }}
                                    label="Start Date"
                                    value={startDate}
                                    onChange={(newValue) => setStartDate(newValue)}
                                />
                            </DemoContainer>
                        </LocalizationProvider>
                    </div>
                    <div className='mb-4'>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={['DatePicker']}>
                                <DatePicker
                                    sx={{ my: 1, width: '60%' }}
                                    label="End Date"
                                    value={endDate}
                                    onChange={(newValue) => setEndDate(newValue)}
                                />
                            </DemoContainer>
                        </LocalizationProvider>
                    </div>
                    <div className='mb-4'>
                        <FormControl sx={{ my: 1, width: '60%' }}>
                            <InputLabel id="report-type-label">Report Type</InputLabel>
                            <Select
                                labelId="report-type-label"
                                id="report-type"
                                value={productType}
                                label="Report Type"
                                onChange={handleProductTypeChange}
                            >
                                <MenuItem value="">
                                    <em>All Leads</em>
                                </MenuItem>
                                <MenuItem value="general_enquiry">General Enquiry</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div className='mb-4'>
                        <FormControl sx={{ my: 1, width: '60%' }} disabled={isGeneralEnquiry}>
                            <InputLabel id="status-filter-label">Status Filter</InputLabel>
                            <Select
                                labelId="status-filter-label"
                                id="status-filter"
                                value={isGeneralEnquiry ? '' : status}
                                label="Status Filter"
                                onChange={handleStatusChange}
                            >
                                <MenuItem value="">
                                    <em>All Statuses</em>
                                </MenuItem>
                                <MenuItem value="new">New</MenuItem>
                                <MenuItem value="quote sent">Quote Sent</MenuItem>
                                <MenuItem value="instructed">Instructed</MenuItem>
                                <MenuItem value="exported">Exported</MenuItem>
                                <MenuItem value="not interested">Not Interested</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div className='mb-4'>
                        <Stack spacing={2} direction="row">
                            <Button
                                variant="contained"
                                onClick={handleGenerateReport}
                                disabled={loading}
                                className='red-bg red-button text-capitalize'
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress size={20} style={{ marginRight: '8px', color: 'white' }} />
                                        Generating...
                                    </>
                                ) : (
                                    'Generate Report'
                                )}
                            </Button>
                        </Stack>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Reports;